import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Building2, ScrollText, LogOut, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { AfyanexLogo } from "@/components/AfyanexLogo";

/** Shell for /platform/* routes. Enforces superadmin-only access via /platform/me. */
export default function PlatformShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [check, setCheck] = useState({ loading: true, ok: false, impersonating: false, denied: false });

  useEffect(() => {
    let alive = true;
    api.get("/platform/me")
      .then(({ data }) => {
        if (!alive) return;
        if (!data.is_superadmin) {
          // Don't bounce — render a clear "not authorized" page. Bouncing
          // through React Router while the avatar menu still offers a
          // "Platform console" link causes a flicker loop.
          setCheck({ loading: false, ok: false, impersonating: false, denied: true });
          return;
        }
        setCheck({ loading: false, ok: true, impersonating: !!data.impersonating, denied: false });
      })
      .catch(() => {
        if (!alive) return;
        navigate("/login", { replace: true });
      });
    return () => { alive = false; };
  }, [navigate]);

  if (check.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg text-clinical-subtext" data-testid="platform-loading">
        Loading platform console…
      </div>
    );
  }
  if (check.denied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg px-6" data-testid="platform-denied">
        <div className="max-w-md w-full card-surface p-8 text-center rounded-2xl shadow-[0_8px_30px_rgba(11,21,48,0.06)]">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-clinical-text tracking-tight">
            Platform console — access restricted
          </h2>
          <p className="mt-2 text-sm text-clinical-subtext">
            This area is for platform administrators only. Your account
            ({user?.email || "current user"}) isn&apos;t in the platform owners list.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/admin", { replace: true })}
              className="btn-primary"
              data-testid="platform-denied-back"
            >
              <ArrowLeft className="h-4 w-4" /> Back to admin app
            </button>
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="btn-ghost text-sm"
              data-testid="platform-denied-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!check.ok) return null;

  const nav = [
    { to: "/platform", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "platform-nav-dashboard" },
    { to: "/platform/agencies", label: "Agencies", icon: Building2, testid: "platform-nav-agencies" },
    { to: "/platform/audit-log", label: "Audit log", icon: ScrollText, testid: "platform-nav-audit" },
  ];

  return (
    <div className="min-h-screen bg-[#0B1530] text-white" data-testid="platform-shell">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0B1530]/95 backdrop-blur">
        <div className="max-w-screen-2xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AfyanexLogo className="h-7 w-auto text-white" />
            <span className="hidden sm:inline text-[11px] uppercase tracking-wider font-bold text-amber-300/90">
              · Platform Console
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link
              to="/admin"
              className="text-white/70 hover:text-white inline-flex items-center gap-1.5"
              data-testid="platform-back-to-admin"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to admin app
            </Link>
            <span className="text-white/40">·</span>
            <span className="text-white/70 hidden md:inline" data-testid="platform-current-user">
              {user?.email}
            </span>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="text-white/70 hover:text-white inline-flex items-center gap-1.5"
              data-testid="platform-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
        {/* Sub-nav */}
        <div className="max-w-screen-2xl mx-auto px-6 flex items-center gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  isActive
                    ? "text-white border-amber-300"
                    : "text-white/60 border-transparent hover:text-white"
                }`
              }
            >
              <n.icon className="h-3.5 w-3.5" /> {n.label}
            </NavLink>
          ))}
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

/** Persistent banner when a superadmin is impersonating an agency admin. */
export function ImpersonationBanner() {
  const [impersonating, setImpersonating] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Read JS-readable flag cookie set by backend at impersonation start
    const has = document.cookie.split(";").some((c) => c.trim().startsWith("hc_impersonating="));
    setImpersonating(has);
  }, []);

  const exit = async () => {
    setExiting(true);
    try {
      await api.post("/platform/exit-impersonation");
      // Return to the agencies list — that's where superadmins start every
      // support session, so it's the most natural place to land after exit.
      window.location.href = "/platform/agencies";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not exit impersonation");
      setExiting(false);
    }
  };

  if (!impersonating) return null;
  return (
    <div
      className="fixed top-0 inset-x-0 z-[200] bg-amber-400 text-amber-950 px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium shadow"
      data-testid="impersonation-banner"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        Platform impersonation active — actions are recorded in the audit log.
      </span>
      <button
        type="button"
        onClick={exit}
        disabled={exiting}
        className="ml-2 rounded-full bg-amber-950 text-amber-100 px-3 py-1 text-xs font-semibold hover:bg-amber-900 disabled:opacity-60"
        data-testid="impersonation-exit"
      >
        {exiting ? "Exiting…" : "Exit impersonation"}
      </button>
    </div>
  );
}
